import { useState, useMemo, useRef, useEffect } from 'react';
import { Send, Pencil, Trash2, Reply, SmilePlus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import { useAddComment, useUpdateComment, useDeleteComment, useToggleCommentReaction } from '../../hooks/useIssues';
import type { IssueComment } from '../../services/issueService';
import type { ProfileColorKey } from '@maios/shared';
import type { ProjectMember } from '../../services/projectService';

const QUICK_EMOJIS = ['👍', '👎', '😀', '🎉', '😢', '❤️', '🚀', '👀'];

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '방금';
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}일 전`;
  return new Date(dateStr).toLocaleDateString('ko-KR');
}

interface Props {
  projectId: string;
  issueId: string;
  comments: IssueComment[] | undefined;
  currentUserId: string;
  members: ProjectMember[];
}

interface AggregatedReaction {
  emoji: string;
  count: number;
  users: string[];     // display names (for tooltip)
  reacted: boolean;    // current user reacted
}

function aggregateReactions(reactions: { emoji: string; userId: string }[] | undefined, currentUserId: string, idToName: Map<string, string>): AggregatedReaction[] {
  if (!reactions || reactions.length === 0) return [];
  const map = new Map<string, { users: string[]; reacted: boolean }>();
  for (const r of reactions) {
    const cur = map.get(r.emoji) ?? { users: [], reacted: false };
    cur.users.push(idToName.get(r.userId) ?? '알 수 없음');
    if (r.userId === currentUserId) cur.reacted = true;
    map.set(r.emoji, cur);
  }
  return [...map.entries()].map(([emoji, v]) => ({ emoji, count: v.users.length, users: v.users, reacted: v.reacted }));
}

export default function IssueComments({ projectId, issueId, comments, currentUserId, members }: Props) {
  const { openProfileModal } = useProfileModal();
  const addMutation = useAddComment(projectId);
  const updateMutation = useUpdateComment(projectId);
  const deleteMutation = useDeleteComment(projectId);
  const reactionMutation = useToggleCommentReaction(projectId);

  const [topText, setTopText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [pickerOpenFor, setPickerOpenFor] = useState<string | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  // 외부 클릭 시 이모지 피커 닫기
  useEffect(() => {
    if (!pickerOpenFor) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpenFor(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpenFor]);

  const idToName = useMemo(() => {
    const m = new Map<string, string>();
    for (const mem of members) m.set(mem.user.id, mem.user.name);
    return m;
  }, [members]);

  // 댓글 트리 구성: 최상위 + 각 부모에 대한 답글
  const { roots, repliesByParent } = useMemo(() => {
    const roots: IssueComment[] = [];
    const repliesByParent = new Map<string, IssueComment[]>();
    for (const c of comments ?? []) {
      if (c.parentCommentId) {
        const list = repliesByParent.get(c.parentCommentId) ?? [];
        list.push(c);
        repliesByParent.set(c.parentCommentId, list);
      } else {
        roots.push(c);
      }
    }
    return { roots, repliesByParent };
  }, [comments]);

  const handleAddTop = () => {
    const content = topText.trim();
    if (!content) return;
    addMutation.mutate(
      { issueId, content },
      {
        onSuccess: () => { setTopText(''); },
        onError: () => toast.error('코멘트 추가 실패'),
      },
    );
  };

  const handleAddReply = (parentId: string) => {
    const content = replyText.trim();
    if (!content) return;
    addMutation.mutate(
      { issueId, content, parentCommentId: parentId },
      {
        onSuccess: () => { setReplyText(''); setReplyingTo(null); },
        onError: () => toast.error('답글 추가 실패'),
      },
    );
  };

  const handleSaveEdit = (commentId: string) => {
    const content = editText.trim();
    if (!content) return;
    updateMutation.mutate(
      { issueId, commentId, content },
      {
        onSuccess: () => { setEditingId(null); setEditText(''); },
        onError: () => toast.error('수정 실패'),
      },
    );
  };

  const handleDelete = (commentId: string) => {
    if (!confirm('이 댓글을 삭제하시겠습니까?')) return;
    deleteMutation.mutate(
      { issueId, commentId },
      { onError: () => toast.error('삭제 실패') },
    );
  };

  const handleToggleReaction = (commentId: string, emoji: string, currentlyReacted: boolean) => {
    reactionMutation.mutate(
      { issueId, commentId, emoji, remove: currentlyReacted },
      { onError: () => toast.error('리액션 처리 실패') },
    );
  };

  const renderEditable = (c: IssueComment) => (
    <div className="space-y-2">
      <textarea
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        rows={2}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing || e.keyCode === 229) return;
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSaveEdit(c.id);
          if (e.key === 'Escape') { setEditingId(null); setEditText(''); }
        }}
      />
      <div className="flex justify-end gap-1.5">
        <button
          onClick={() => { setEditingId(null); setEditText(''); }}
          className="px-2.5 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          취소
        </button>
        <button
          onClick={() => handleSaveEdit(c.id)}
          disabled={!editText.trim() || updateMutation.isPending}
          className="px-3 py-1 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          저장
        </button>
      </div>
    </div>
  );

  const renderComment = (c: IssueComment, isReply: boolean) => {
    const aggregated = aggregateReactions(c.reactions, currentUserId, idToName);
    const isOwn = c.author.id === currentUserId;
    const isEdited = c.updatedAt && c.createdAt && c.updatedAt !== c.createdAt;

    return (
      <div key={c.id} className={`flex gap-2 ${isReply ? 'ml-8 pt-2' : ''}`}>
        <Avatar
          size="sm"
          userId={c.author.id}
          name={c.author.name}
          avatarUrl={c.author.avatarUrl}
          displayColor={c.author.displayColor as ProfileColorKey | null | undefined}
          firstName={c.author.firstName}
          onClick={() => openProfileModal(c.author.id)}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{c.author.name}</span>
            <span className="text-sm text-gray-400">{formatRelativeTime(c.createdAt)}</span>
            {isEdited && <span className="text-[11px] text-gray-400">(수정됨)</span>}
          </div>

          {editingId === c.id ? (
            renderEditable(c)
          ) : (
            <div className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{c.content}</div>
          )}

          {/* 리액션 배지 */}
          {aggregated.length > 0 && editingId !== c.id && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {aggregated.map((r) => (
                <button
                  key={r.emoji}
                  onClick={() => handleToggleReaction(c.id, r.emoji, r.reacted)}
                  title={r.users.join(', ')}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm border transition-colors ${r.reacted
                    ? 'bg-primary-50 border-primary-300 text-primary-700'
                    : 'bg-gray-50 border-transparent hover:border-gray-300 text-gray-600'
                    }`}
                >
                  <span>{r.emoji}</span>
                  <span>{r.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* 액션 바 */}
          {editingId !== c.id && (
            <div className="flex items-center gap-1 mt-1 text-sm text-gray-400 relative">
              <button
                onClick={() => setPickerOpenFor((cur) => (cur === c.id ? null : c.id))}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="리액션 추가"
              >
                <SmilePlus size={12} />
              </button>
              {!isReply && (
                <button
                  onClick={() => { setReplyingTo(c.id); setReplyText(''); }}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <Reply size={12} />
                  답글
                </button>
              )}
              {isOwn && (
                <>
                  <button
                    onClick={() => { setEditingId(c.id); setEditText(c.content); }}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  >
                    <Pencil size={12} />
                    수정
                  </button>
                  <button
                    onClick={() => handleDelete(c.id)}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 size={12} />
                    삭제
                  </button>
                </>
              )}

              {pickerOpenFor === c.id && (
                <div
                  ref={pickerRef}
                  className="absolute top-full left-0 mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex gap-1"
                >
                  {QUICK_EMOJIS.map((emoji) => {
                    const reacted = aggregated.find((a) => a.emoji === emoji)?.reacted ?? false;
                    return (
                      <button
                        key={emoji}
                        onClick={() => { handleToggleReaction(c.id, emoji, reacted); setPickerOpenFor(null); }}
                        className="size-7 flex items-center justify-center text-base hover:bg-gray-100 rounded transition-colors"
                      >
                        {emoji}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPickerOpenFor(null)}
                    className="size-7 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded transition-colors"
                    title="닫기"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 답글 입력 */}
          {replyingTo === c.id && (
            <div className="mt-2 space-y-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={2}
                placeholder="답글을 입력하세요..."
                autoFocus
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddReply(c.id);
                  if (e.key === 'Escape') { setReplyingTo(null); setReplyText(''); }
                }}
              />
              <div className="flex justify-end gap-1.5">
                <button
                  onClick={() => { setReplyingTo(null); setReplyText(''); }}
                  className="px-2.5 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={() => handleAddReply(c.id)}
                  disabled={!replyText.trim() || addMutation.isPending}
                  className="px-3 py-1 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  답글
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {roots.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4">코멘트가 없습니다.</p>
      )}
      {roots.map((c) => (
        <div key={c.id}>
          {renderComment(c, false)}
          {(repliesByParent.get(c.id) ?? []).map((reply) => renderComment(reply, true))}
        </div>
      ))}

      {/* 최상위 댓글 입력 */}
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <textarea
          value={topText}
          onChange={(e) => setTopText(e.target.value)}
          placeholder="코멘트를 입력하세요..."
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
          rows={2}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddTop();
          }}
        />
        <button
          onClick={handleAddTop}
          disabled={!topText.trim() || addMutation.isPending}
          className="self-end p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          title="등록 (⌘/Ctrl+Enter)"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
